import React from 'react';
import { ChevronDown } from 'lucide-react';
import { getToolSeo } from '../seo';
import { TOOLS, POPULAR_SLUGS, getToolBySlug } from '../registry';
import { buildToolUrl } from '../../lib/subdomain';

// Renders the visible SEO block that sits at the bottom of every tool page:
//   1. HowTo (ordered steps)
//   2. FAQ (collapsible <details>)
//   3. Related tools
//
// Content comes from frontend/src/tools/seo.js — the same source that drives
// the JSON-LD in <head>. Keeping the visible copy in sync with the JSON-LD is
// a Google rich-result requirement; duplicating the source here avoids drift.

function pickRelated(slug, count = 4) {
  const tool = getToolBySlug(slug);
  if (!tool) return [];
  const sameGroup = TOOLS.filter((t) => t.group === tool.group && t.slug !== slug);
  const pool = [...sameGroup];
  if (pool.length < count) {
    const popularFill = POPULAR_SLUGS
      .map((s) => getToolBySlug(s))
      .filter((t) => t && t.slug !== slug && !pool.find((p) => p.slug === t.slug));
    pool.push(...popularFill);
  }
  // Final fallback: anything else live, to guarantee 4 tiles.
  if (pool.length < count) {
    const fill = TOOLS.filter(
      (t) => t.slug !== slug && !pool.find((p) => p.slug === t.slug)
    );
    pool.push(...fill);
  }
  return pool.slice(0, count);
}

function HowToSection({ howTo }) {
  if (!howTo || !howTo.steps?.length) return null;
  return (
    <section className="mt-12" aria-labelledby="howto-heading">
      <h2 id="howto-heading" className="text-2xl font-bold tracking-tight">
        {howTo.name}
      </h2>
      {howTo.description ? (
        <p className="mt-2 text-muted-foreground">{howTo.description}</p>
      ) : null}
      <ol className="mt-6 space-y-4">
        {howTo.steps.map((step, i) => (
          <li
            key={i}
            className="flex gap-4 rounded-lg border border-border bg-card p-4"
          >
            <div className="h-8 w-8 shrink-0 rounded-full bg-orange-500/10 text-orange-500 text-sm font-bold flex items-center justify-center">
              {i + 1}
            </div>
            <div className="min-w-0">
              <div className="font-semibold">{step.name}</div>
              <p className="mt-1 text-sm text-muted-foreground">{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function FaqSection({ faqs, toolName }) {
  if (!faqs?.length) return null;
  return (
    <section className="mt-12" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="text-2xl font-bold tracking-tight">
        {toolName} — Frequently Asked Questions
      </h2>
      <div className="mt-6 rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
        {faqs.map((f, i) => (
          <details key={i} className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 text-left font-medium hover:bg-muted/40">
              <span>{f.q}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4 text-sm text-muted-foreground">{f.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

function RelatedTools({ slug }) {
  const related = pickRelated(slug, 4);
  if (!related.length) return null;
  return (
    <section className="mt-12" aria-labelledby="related-heading">
      <h2 id="related-heading" className="text-2xl font-bold tracking-tight">
        Related PDF tools
      </h2>
      <p className="mt-2 text-muted-foreground">
        Keep your workflow in one place.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {related.map((t) => (
          <a
            key={t.slug}
            href={buildToolUrl(t.slug)}
            className="group rounded-xl border border-border bg-card p-4 transition-colors hover:border-orange-500/60 hover:bg-orange-500/5"
            data-testid={`related-tool-${t.slug}`}
          >
            <div className="text-2xl" aria-hidden>{t.icon}</div>
            <div className="mt-2 font-semibold group-hover:text-orange-500">
              {t.name}
            </div>
            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
              {t.short}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function ToolSeoBlock({ tool }) {
  if (!tool?.slug) return null;
  const seo = getToolSeo(tool.slug);
  // Always render Related tools, even if a tool has no SEO content yet —
  // internal linking is the most important piece for SEO.
  return (
    <div className="mt-4">
      {seo?.intro ? (
        <section className="mt-2" aria-labelledby="about-heading">
          <h2 id="about-heading" className="sr-only">About {tool.name}</h2>
          <p className="text-base text-muted-foreground leading-relaxed max-w-3xl">
            {seo.intro}
          </p>
        </section>
      ) : null}
      <HowToSection howTo={seo?.howTo} />
      <FaqSection faqs={seo?.faqs} toolName={tool.name} />
      <RelatedTools slug={tool.slug} />
    </div>
  );
}

export default ToolSeoBlock;
